import type { ComponentPropsWithoutRef } from 'react';
import { PreviewCard as BasePreviewCard } from '@base-ui/react/preview-card';
import { merge } from 'lodash-es';
import React from 'react';

import type { GutterProps } from '../../utilities/shared/Gutter';
import type { MaxWidthProps } from '../../utilities/shared/MaxWidth';
import type { MinWidthProps } from '../../utilities/shared/MinWidth';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { WidthProps } from '../../utilities/shared/Width';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';

export const createPreviewCardHandle = BasePreviewCard.createHandle;

const PreviewCardPopover = styled(BasePreviewCard.Popup, {
  display: 'flex',
  flexDirection: 'column',
  minWidth: '$240',
  maxWidth: '$360',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    transition: 'opacity 150ms ease-out, transform 150ms ease-out',

    '&[data-starting-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },

    '&[data-ending-style]': {
      opacity: 0,
      transform: 'translateY(4px) scale(0.98)',
    },
  },
});
PreviewCardPopover.displayName = 'PreviewCardPopover';

const PreviewCardPositioner = styled(BasePreviewCard.Positioner, {
  zIndex: zIndexes.overlay,
});
PreviewCardPositioner.displayName = 'PreviewCardPositioner';

const PreviewCardPortal = styled(BasePreviewCard.Portal);
PreviewCardPortal.displayName = 'PreviewCardPortal';

export type PreviewCardContentProps = {
  /**
   * How to align the popup relative to the trigger. Defaults to `'center'`.
   */
  alignment?: 'start' | 'center' | 'end';
  /**
   * The content to display inside the preview card popup.
   */
  children: React.ReactNode;
  /**
   * The amount of padding to add to the popup to prevent it from being cut off by the viewport. Defaults to `12`.
   */
  collisionPadding?: SpacingScale;
  /**
   * The amount of spacing to add between the content and the popup. Defaults to `12`.
   */
  gap?: SpacingScale;
  /**
   * Which side of the trigger to display the popup. Defaults to `'bottom'`.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Distance between the trigger and the popup in pixels. Defaults to `8`.
   */
  sideOffset?: SpacingScale;
} & GutterProps &
  WidthProps &
  MaxWidthProps &
  MinWidthProps;

export function PreviewCardContent({
  alignment = 'center',
  children,
  collisionPadding = 12,
  context = 'content',
  gap = 12,
  gutter = 'all',
  side = 'bottom',
  sideOffset = 8,
  width,
  maxWidth,
  minWidth,
}: PreviewCardContentProps) {
  const previewCardPopoverCSS = merge(
    {
      maxWidth: 'var(--available-width)',
      maxHeight: 'var(--available-height)',
    },
    spacingCSS(gap),
    gutterCSS({ context, gutter }),
    widthCSS(width),
    maxWidth ? maxWidthCSS(maxWidth) : undefined,
    minWidthCSS(minWidth),
  );

  return (
    <PreviewCardPortal>
      <PreviewCardPositioner
        side={side}
        sideOffset={sideOffset}
        align={alignment}
        collisionPadding={collisionPadding}
      >
        <PreviewCardPopover css={previewCardPopoverCSS}>{children}</PreviewCardPopover>
      </PreviewCardPositioner>
    </PreviewCardPortal>
  );
}

export type PreviewCardViewportProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Viewport>;

export const PreviewCardViewport = styled(BasePreviewCard.Viewport);
PreviewCardViewport.displayName = 'PreviewCardViewport';

export type PreviewCardTriggerProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Trigger>;

export const PreviewCardTriggerContainer = styled(BasePreviewCard.Trigger);
PreviewCardTriggerContainer.displayName = 'PreviewCardTriggerContainer';

export function PreviewCardTrigger({
  closeDelay = 600,
  delay = 100,
  ...props
}: PreviewCardTriggerProps) {
  return <PreviewCardTriggerContainer closeDelay={closeDelay} delay={delay} {...props} />;
}

export type PreviewCardProps = ComponentPropsWithoutRef<typeof BasePreviewCard.Root>;

export const PreviewCard = styled(BasePreviewCard.Root);
PreviewCard.displayName = 'PreviewCard';
